import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Select } from '@servicecompressionllc/sc-design-system';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { AppShell } from '../../components/AppShell.js';
import { useMe } from '../../hooks/useMe.js';
import { apiRequest } from '../../lib/apiClient.js';
import { DeviceNotifications } from '../push/DeviceNotifications.js';
import { notificationErrorMessage } from './feedback.js';

type Choice = { connectionId: string } | { disabled: true } | null;
type Policy = { default: Choice };
interface Preferences {
  policy: Policy;
  organization: Policy;
  connections: { id: string; name: string; key: string; isActive: boolean; ready: boolean }[];
  effective: Choice;
}
const encode = (choice: Choice) =>
  choice === null ? 'inherit' : 'disabled' in choice ? 'off' : choice.connectionId;
const decode = (value: string): Choice =>
  value === 'inherit' ? null : value === 'off' ? { disabled: true } : { connectionId: value };

export const NotificationPreferencesPage = ({
  organization = false,
  embedded = false,
}: {
  organization?: boolean;
  embedded?: boolean;
}) => {
  const me = useMe();
  const queryClient = useQueryClient();
  const queryKey = ['notification-preferences', me.data?.tenant.id, me.data?.user.id];
  const query = useQuery({
    queryKey,
    enabled: Boolean(me.data),
    queryFn: () => apiRequest<Preferences>('/api/notifications/preferences'),
  });
  const [draft, setDraft] = useState<Policy | null>(null);
  const save = useMutation({
    mutationFn: (body: Policy) =>
      apiRequest<Policy>(
        organization ? '/api/notifications/defaults' : '/api/notifications/preferences',
        { method: 'PUT', body },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      setDraft(null);
    },
  });
  const test = useMutation({
    mutationFn: () =>
      apiRequest<{ outcome: string }>('/api/notifications/test-me', { method: 'POST' }),
  });
  const teamsConnection = query.data?.connections.find(
    (connection) => connection.key === 'teams_dm' && connection.isActive,
  );
  const installationKey = [...queryKey, 'teams-app', teamsConnection?.id];
  const installation = useQuery({
    queryKey: installationKey,
    enabled: !organization && Boolean(teamsConnection),
    queryFn: () => apiRequest<{ installed: boolean | null }>('/api/notifications/teams-app'),
    retry: false,
  });
  const install = useMutation({
    mutationFn: () =>
      apiRequest<{ installed: boolean; alreadyInstalled: boolean }>(
        '/api/notifications/teams-app',
        { method: 'POST', body: {} },
      ),
    onSuccess: async () => {
      test.reset();
      queryClient.setQueryData(installationKey, { installed: true });
      await queryClient.invalidateQueries({ queryKey });
    },
  });
  const data = query.data;
  const policy = draft ?? (organization ? data?.organization : data?.policy);
  const options = [
    { value: 'inherit', label: organization ? 'No default selected' : 'Use organization default' },
    ...(data?.connections ?? [])
      .filter((connection) => connection.isActive)
      .map((connection) => ({
        value: connection.id,
        label: connection.name || 'Microsoft Teams',
      })),
    ...(organization ? [] : [{ value: 'off', label: 'Off' }]),
  ];
  const current = encode(policy?.default ?? null);
  if (!options.some((option) => option.value === current)) {
    options.push({ value: current, label: 'Connection unavailable — choose another' });
  }
  const Shell = embedded ? Fragment : AppShell;
  return (
    <Shell>
      {organization && (
        <Breadcrumbs
          trail={[
            { label: 'Capabilities', to: '/capabilities' },
            { label: 'Notifications', to: '/capabilities/notifications' },
          ]}
        />
      )}
      <div className="max-w-2xl space-y-5">
        {!embedded && (
          <h1 className="text-2xl font-bold text-ink">
            {organization ? 'Notification defaults' : 'Notifications'}
          </h1>
        )}
        <p className="text-sm text-ink-muted">
          {organization
            ? 'Choose how people receive notifications when they have not set a personal preference.'
            : 'Choose how you receive notifications. Approval requests remain enabled.'}
        </p>
        {!organization && <DeviceNotifications />}
        {query.isLoading && (
          <p className="text-sm text-ink-muted">Loading notification settings…</p>
        )}
        {query.error && (
          <Banner variant="danger" role="alert">
            <Banner.Body>
              <Banner.Title>Could not load notification settings</Banner.Title>
              <Banner.Description>
                {notificationErrorMessage(query.error, 'Please refresh the page and try again.')}
              </Banner.Description>
            </Banner.Body>
          </Banner>
        )}
        {data && policy && (
          <>
            <div className="rounded-xl border border-outline bg-surface p-5 space-y-4">
              <label className="grid gap-1 text-sm text-ink">
                Delivery
                <Select
                  aria-label="Delivery"
                  value={[current]}
                  options={options}
                  onChange={(value) => {
                    save.reset();
                    test.reset();
                    setDraft({ default: decode(value[0] ?? 'inherit') });
                  }}
                />
              </label>
              <p className="text-xs text-ink-muted">
                Turning off delivery affects optional notifications. Approval requests still use an
                available configured default.
              </p>
            </div>
            {!organization && (
              <div className="text-sm text-ink-muted space-y-2">
                {data.connections.map((connection) => (
                  <p key={connection.id}>
                    {connection.name || 'Microsoft Teams'}:{' '}
                    {!connection.isActive
                      ? 'disabled by your organization'
                      : connection.ready
                        ? 'ready for personal messages'
                        : installation.data?.installed === false
                          ? 'install the Teams app to receive personal messages'
                          : installation.data?.installed === true
                            ? 'app installed; messaging connection is not ready yet'
                            : 'app installation has not been confirmed'}
                    .
                  </p>
                ))}
                {!data.connections.length && (
                  <p>Your organization has not configured personal notifications yet.</p>
                )}
              </div>
            )}
            {!organization &&
              Boolean(teamsConnection) &&
              !installation.isFetching &&
              !installation.isError &&
              installation.data?.installed === false && (
                <Button
                  variant="secondary"
                  disabled={install.isPending}
                  onClick={() => {
                    save.reset();
                    test.reset();
                    install.mutate();
                  }}
                >
                  {install.isPending ? 'Installing Teams app…' : 'Install Teams app'}
                </Button>
              )}
            {install.error && (
              <Banner variant="danger" role="alert">
                <Banner.Body>
                  <Banner.Title>Could not install Teams app</Banner.Title>
                  <Banner.Description>
                    {notificationErrorMessage(
                      install.error,
                      'Please try again. If installation is blocked, ask your administrator to check your organization’s Teams app permissions.',
                    )}
                  </Banner.Description>
                </Banner.Body>
              </Banner>
            )}
            {install.isSuccess && (
              <Banner variant="success" role="status">
                <Banner.Body>
                  <Banner.Title>Teams app installed</Banner.Title>
                  <Banner.Description>
                    {data.connections.some(
                      (connection) => connection.key === 'teams_dm' && connection.ready,
                    )
                      ? 'You can now send yourself a test notification.'
                      : 'Your messaging connection is still being set up. Refresh this page in a moment to check it.'}
                  </Banner.Description>
                </Banner.Body>
              </Banner>
            )}
            {save.error && (
              <Banner variant="danger" role="alert">
                <Banner.Body>
                  <Banner.Title>Could not save preferences</Banner.Title>
                  <Banner.Description>
                    {notificationErrorMessage(
                      save.error,
                      'Your changes have not been saved. Please try again.',
                    )}
                  </Banner.Description>
                </Banner.Body>
              </Banner>
            )}
            {save.isSuccess && !draft && (
              <Banner variant="success" role="status">
                <Banner.Body>
                  <Banner.Title>Preferences saved</Banner.Title>
                </Banner.Body>
              </Banner>
            )}
            <div className="flex gap-3">
              <Button
                disabled={!draft || save.isPending}
                onClick={() => {
                  test.reset();
                  save.mutate(policy);
                }}
              >
                Save preferences
              </Button>
              {!organization && (
                <Button
                  variant="secondary"
                  disabled={Boolean(draft) || test.isPending}
                  onClick={() => {
                    save.reset();
                    test.mutate();
                  }}
                >
                  Send test to me
                </Button>
              )}
            </div>
            {!organization && draft && (
              <p className="text-xs text-ink-muted">Save your changes before sending a test.</p>
            )}
            {test.error && (
              <Banner variant="danger" role="alert">
                <Banner.Body>
                  <Banner.Title>Test notification could not be sent</Banner.Title>
                  <Banner.Description>
                    {notificationErrorMessage(
                      test.error,
                      'Please try again. If the problem continues, ask your administrator to check your notification connection.',
                    )}
                  </Banner.Description>
                </Banner.Body>
              </Banner>
            )}
            {test.data && (
              <Banner variant={test.data.outcome === 'sent' ? 'success' : 'info'} role="status">
                <Banner.Body>
                  <Banner.Title>
                    {test.data.outcome === 'sent'
                      ? 'Test notification sent'
                      : test.data.outcome === 'dry-run'
                        ? 'Test simulated'
                        : 'Test notification not sent'}
                  </Banner.Title>
                  <Banner.Description>
                    {test.data.outcome === 'sent'
                      ? 'Check your messages for the test notification.'
                      : test.data.outcome === 'dry-run'
                        ? 'Test simulated; no message was sent.'
                        : test.data.outcome === 'suppressed'
                          ? 'Notifications are turned off in your delivery settings.'
                          : 'Your account is not available for notifications. Contact your administrator.'}
                  </Banner.Description>
                </Banner.Body>
              </Banner>
            )}
          </>
        )}
      </div>
    </Shell>
  );
};
